import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Download } from 'lucide-react'
import type { Message, Artifact, Participant } from '../../types'
import { getSessionMessages, getSessionArtifacts, getSessionParticipants, getSessionReflections } from '../../lib/organization'
import { getSession } from '../../lib/session'
import { downloadSessionBundle } from '../../lib/download'
import MessageList from '../chat/MessageList'

export default function OrgSessionViewer() {
  const { orgId, sessionId } = useParams<{ orgId: string; sessionId: string }>()
  const navigate = useNavigate()
  const [messages, setMessages] = useState<Message[]>([])
  const [artifacts, setArtifacts] = useState<Artifact[]>([])
  const [sessionName, setSessionName] = useState('session')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    if (!sessionId) return
    Promise.all([
      getSessionMessages(sessionId),
      getSessionArtifacts(sessionId),
      getSession(sessionId),
    ]).then(([msgs, arts, session]) => {
      setMessages(msgs)
      setArtifacts(arts)
      if (session?.sessionName) setSessionName(session.sessionName)
    }).catch((err) => {
      console.error('Failed to load session:', err)
      setLoadError(true)
    }).finally(() => setLoading(false))
  }, [sessionId])

  async function handleDownload() {
    if (!sessionId) return
    setDownloading(true)
    try {
      const [sessionParticipants, reflections] = await Promise.all([
        getSessionParticipants(sessionId),
        getSessionReflections(sessionId),
      ])
      await downloadSessionBundle(sessionName, messages, artifacts, sessionParticipants, reflections)
    } catch (err) {
      console.error('Bundle download failed:', err)
    } finally {
      setDownloading(false)
    }
  }

  // Build a minimal participants list from message data for MessageList
  const participantMap = new Map<string, Participant>()
  for (const m of messages) {
    if (m.participantId !== 'agent' && m.participantId !== 'system' && !participantMap.has(m.participantId)) {
      participantMap.set(m.participantId, {
        id: m.participantId,
        displayName: m.displayName,
        joinedAt: m.timestamp,
        isTyping: false,
        color: '#6366f1',
      })
    }
  }
  const participants = Array.from(participantMap.values())

  if (!loading && loadError) {
    return (
      <div className="min-h-screen bg-stone flex items-center justify-center px-4">
        <div className="text-center space-y-2">
          <p className="hl text-xl text-navy">Session unavailable</p>
          <p className="text-sm text-navy/70">You may not have access to this session.</p>
          <button onClick={() => navigate(`/org/${orgId}`)} className="text-sm text-amber hover:text-amber-dark">
            Back to team
          </button>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-stone flex items-center justify-center">
        <p className="mono-label text-navy/70">
          Loading the session
          <span className="caret" aria-hidden="true" />
        </p>
      </div>
    )
  }

  return (
    <div className="h-dvh flex flex-col bg-stone">
      <header className="bg-navy text-stone px-4 py-3 flex items-center justify-between shrink-0 border-b-2 border-golden">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/org/${orgId}`)}
            className="p-1.5 hover:bg-white/10 transition-colors cursor-pointer"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="hl text-lg text-stone">Session viewer</h1>
        </div>
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="flex items-center gap-1.5 mono-label border-2 border-stone/55 px-3 py-2 hover:bg-white/10 transition disabled:opacity-50 cursor-pointer"
        >
          <Download className="w-4 h-4" />
          {downloading ? 'Downloading...' : 'Download Bundle'}
        </button>
      </header>

      <MessageList
        messages={messages}
        participants={participants}
        artifacts={artifacts}
        currentParticipantId=""
      />
    </div>
  )
}
